import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from './AuthContext';

const ProfileContext = createContext();

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

export const ProfileProvider = ({ children }) => {
  const [activeProfile, setActiveProfile] = useState(null);
  const [addedToProfile, setAddedToProfile] = useState(new Set());
  const [addedWorkflowsToProfile, setAddedWorkflowsToProfile] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const fetchActiveProfileRef = useRef(null);
  const lastFetchTimeRef = useRef(0);

  const fetchActiveProfile = useCallback(async () => {
    // Rate limiting: prevent fetching more than once every 2 seconds
    const now = Date.now();
    if (now - lastFetchTimeRef.current < 2000) {
      return;
    }
    lastFetchTimeRef.current = now;

    if (!user) {
      setActiveProfile(null);
      setAddedToProfile(new Set());
      setAddedWorkflowsToProfile(new Set());
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await apiClient.get('/profiles');
      const profiles = response.data.profiles; // Changed from response.data.data to response.data.profiles
      const active = profiles.find(profile => profile.isActive);
      
      console.log('ProfileContext - Fetched profiles:', profiles);
      console.log('ProfileContext - Active profile:', active);
      
      setActiveProfile(active);
      
      if (active && active.mcpServers) {
        const serverIds = active.mcpServers.map(({ mcpServer }) => mcpServer.id);
        console.log('ProfileContext - Server IDs from active profile:', serverIds);
        setAddedToProfile(new Set(serverIds));
      } else {
        console.log('ProfileContext - No active profile or no mcpServers');
        setAddedToProfile(new Set());
      }
      
      if (active && active.workflows) {
        const workflowIds = active.workflows.map(({ workflow }) => workflow.id);
        console.log('ProfileContext - Workflow IDs from active profile:', workflowIds);
        setAddedWorkflowsToProfile(new Set(workflowIds));
      } else {
        console.log('ProfileContext - No active profile or no workflows');
        setAddedWorkflowsToProfile(new Set());
      }
    } catch (err) {
      console.error('Error fetching active profile:', err);
      setActiveProfile(null);
      setAddedToProfile(new Set());
      setAddedWorkflowsToProfile(new Set());
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Store the latest fetch function in a ref
  useEffect(() => {
    fetchActiveProfileRef.current = fetchActiveProfile;
  }, [fetchActiveProfile]);

  useEffect(() => {
    fetchActiveProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Refresh profile state when page becomes active (user navigates between pages)
  // Debounced to prevent too many requests
  useEffect(() => {
    let timeoutId;
    const MIN_FETCH_INTERVAL = 10000; // Minimum 10 seconds between fetches from event listeners

    const handleFocus = () => {
      if (user && fetchActiveProfileRef.current) {
        const now = Date.now();
        if (now - lastFetchTimeRef.current > MIN_FETCH_INTERVAL) {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            if (fetchActiveProfileRef.current) {
              fetchActiveProfileRef.current();
            }
          }, 2000); // Debounce by 2 seconds
        }
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && user && fetchActiveProfileRef.current) {
        const now = Date.now();
        if (now - lastFetchTimeRef.current > MIN_FETCH_INTERVAL) {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            if (fetchActiveProfileRef.current) {
              fetchActiveProfileRef.current();
            }
          }, 2000); // Debounce by 2 seconds
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const addMCPServerToProfile = useCallback(async (mcpServerId) => {
    try {
      const response = await apiClient.post('/profiles/add-mcp-server', { mcpServerId });
      
      // Update local state immediately
      setAddedToProfile(prev => new Set([...prev, mcpServerId]));
      
      // Refresh profile data to ensure consistency
      await fetchActiveProfile();
      
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('Error adding MCP server to active profile:', error);
      // If the error is that the item already exists, treat it as success
      if (error.response?.data?.error?.includes('already in the active profile')) {
        // Update local state to reflect that it's already in profile
        setAddedToProfile(prev => new Set([...prev, mcpServerId]));
        return { 
          success: true, 
          message: 'MCP Server was already in the active profile' 
        };
      }
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to add MCP server to active profile' 
      };
    }
  }, [fetchActiveProfile]);

  const removeMCPServerFromProfile = useCallback(async (mcpServerId) => {
    try {
      // Update local state immediately
      setAddedToProfile(prev => {
        const newSet = new Set(prev);
        newSet.delete(mcpServerId);
        return newSet;
      });
      
      // Refresh profile data to ensure consistency
      await fetchActiveProfile();
      
      return { success: true };
    } catch (error) {
      console.error('Error removing MCP server from active profile:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to remove MCP server from active profile' 
      };
    }
  }, [fetchActiveProfile]);

  const isInActiveProfile = useCallback((mcpServerId) => {
    const isInProfile = addedToProfile.has(mcpServerId);
    console.log(`ProfileContext - isInActiveProfile(${mcpServerId}):`, isInProfile, 'addedToProfile:', Array.from(addedToProfile));
    return isInProfile;
  }, [addedToProfile]);

  const addWorkflowToProfile = useCallback(async (workflowId) => {
    try {
      const response = await apiClient.post('/profiles/add-workflow', { workflowId });
      
      // Update local state immediately
      setAddedWorkflowsToProfile(prev => {
        const newSet = new Set(prev);
        newSet.add(workflowId);
        return newSet;
      });
      
      // Refresh profile data to ensure consistency
      await fetchActiveProfile();
      
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('Error adding workflow to active profile:', error);
      // If the error is that the item already exists, treat it as success
      if (error.response?.data?.error?.includes('already in the active profile')) {
        // Update local state to reflect that it's already in profile
        setAddedWorkflowsToProfile(prev => {
          const newSet = new Set(prev);
          newSet.add(workflowId);
          return newSet;
        });
        return { 
          success: true, 
          message: 'Workflow was already in the active profile' 
        };
      }
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to add workflow to active profile' 
      };
    }
  }, [fetchActiveProfile]);

  const removeWorkflowFromProfile = useCallback(async (workflowId) => {
    try {
      // Update local state immediately
      setAddedWorkflowsToProfile(prev => {
        const newSet = new Set(prev);
        newSet.delete(workflowId);
        return newSet;
      });
      
      // Refresh profile data to ensure consistency
      await fetchActiveProfile();
      
      return { success: true };
    } catch (error) {
      console.error('Error removing workflow from active profile:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to remove workflow from active profile' 
      };
    }
  }, [fetchActiveProfile]);

  const isWorkflowInActiveProfile = useCallback((workflowId) => {
    const isInProfile = addedWorkflowsToProfile.has(workflowId);
    console.log(`ProfileContext - isWorkflowInActiveProfile(${workflowId}):`, isInProfile, 'addedWorkflowsToProfile:', Array.from(addedWorkflowsToProfile));
    return isInProfile;
  }, [addedWorkflowsToProfile]);

  const refreshProfile = useCallback(() => {
    return fetchActiveProfile();
  }, [fetchActiveProfile]);

  const getProfileMCPServerCount = useCallback(() => {
    if (!activeProfile || !activeProfile.mcpServers) return 0;
    return activeProfile.mcpServers.length;
  }, [activeProfile]);

  const getProfileWorkflowCount = useCallback(() => {
    if (!activeProfile || !activeProfile.workflows) return 0;
    return activeProfile.workflows.length;
  }, [activeProfile]);

  const value = {
    activeProfile,
    addedToProfile,
    addedWorkflowsToProfile,
    loading,
    addMCPServerToProfile,
    removeMCPServerFromProfile,
    isInActiveProfile,
    addWorkflowToProfile,
    removeWorkflowFromProfile,
    isWorkflowInActiveProfile,
    refreshProfile,
    getProfileMCPServerCount,
    getProfileWorkflowCount
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};
